import React from "react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import { UploadedSellsheet } from "../sellsheets/UploadedSellsheet";
import { UploadedRules } from "../rules/UploadedRules";
import { enqueueSnackbar } from "notistack";
import { UploadedSizzle } from "../sizzle/UploadedSizzle";
import { isStringEmpty } from "../core/validation";
import { useGameIdFromRoute } from "./WithGameIdFromRoute";
import { PublicGameDetail } from "./PublicGameDetail";
import { useGame, useMyGames, useUpdateGameV2 } from "@seabrookstudios/pitch2table";
import { GameCardCover } from "./CoverImages";
import { ContractCardForGame } from "./contracts/ContractCardForGame";
import { LastUpdated } from "../core/LastUpdated";
import { PublisherInterestForGameMenuItem } from "./PublisherInterestForGameMenuItem";
import { EditableInternalId } from "./InternalId";
import { GameDesignersMenuItem } from "./GameDesignersMenuItem";
import { EditableHeading } from "./EditableHeading";
import { EditableListItem } from "./EditableListItem";
import { EditableTagsListItem } from "./EditableTagsListItem";
import { ArrowListItem } from "../core/ArrowListItem";
import { Path } from "../Path";
import { Features } from "./features";
import { RoyaltyCardForGame } from "./royalties/RoyaltyCardForGame";
import { GameStatusCard } from "./GameStatusCard";
import { MessageBoardMenuItem } from "./MessageBoardMenuItem";
import { StarGoalButton } from "./StarGoalButton";
import { ArchiveControl } from "./ArchiveControl";
import { useDocumentTitle } from "usehooks-ts";
import { PlayTestsMenuItem } from "./PlayTestsMenuItem";
import { GameYearStarted } from "./GameYearStarted";
import { GameSlugs } from "./GameSlug";
import { showContracts } from "@seabrookstudios/pitch2table-core";
import { CardLoading } from "../core/CardLoading";
import { EditPlayersControl } from "./EditPlayersControls";
import { EditAgeControl } from "./EditAgeControl";
import { EditDurationControl } from "./EditDurationControl";
import { ConventionsMenuItem } from "./ConventionsMenuItem";
import { GameLinksCard } from "./GameLinksCard";
import { LinkedGamesCard } from "./LinkedGamesCard";
import { GameAwardsCard } from "./GameAwardsCard";
import { BggRankField } from "./BggRankField";
import { GameEditionsCard } from "./GameEditionsCard";
import { GameTypeControl } from "./GameTypeControl";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const GameDetail = ({ gameId }) => {
  const { isLoading, data: game, error } = useGame(gameId);

  const onUpdateSuccess = () => {
    enqueueSnackbar("Saved", { variant: "success" });
  };

  const onUpdateFailure = () => {
    enqueueSnackbar("Update failed", { variant: "error" });
  };

  const updateGame = useUpdateGameV2(gameId, onUpdateSuccess, onUpdateFailure);
  /**
   * @param {string} name
   */
  const updateName = async (name) => {
    if (isStringEmpty(name)) {
      return;
    }

    await updateGame.mutateAsync({
      name,
      sizzle: undefined,
      bgg: undefined,
      personalGoal: undefined,
      customAuthors: undefined,
      gameType: undefined,
      referenceId: undefined,
      tags: undefined,
      feedbackEnabled: undefined,
    });
  };

  /**
   * @param {string} tag
   */
  const updateTag = async (tag) => {
    if (isStringEmpty(tag)) {
      return;
    }

    await updateGame.mutateAsync({
      tag,
      sizzle: undefined,
      bgg: undefined,
      personalGoal: undefined,
      customAuthors: undefined,
      gameType: undefined,
      referenceId: undefined,
      tags: undefined,
      feedbackEnabled: undefined,
    });
  };

  /**
   * @param {string} description
   */
  const updateDescription = async (description) => {
    if (isStringEmpty(description)) {
      return;
    }

    await updateGame.mutateAsync({
      description,
      sizzle: undefined,
      bgg: undefined,
      personalGoal: undefined,
      customAuthors: undefined,
      gameType: undefined,
      referenceId: undefined,
      tags: undefined,
      feedbackEnabled: undefined,
    });
  };

  /**
   * @param {string|number|undefined} tags
   */
  const updateTags = async (tags) => {
    await updateGame.mutateAsync({
      tags: tags == null ? "" : String(tags),
      sizzle: undefined,
      bgg: undefined,
      personalGoal: undefined,
      customAuthors: undefined,
      gameType: undefined,
      referenceId: undefined,
      feedbackEnabled: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").DesignerGameStatus} status
   */
  const updateStatus = async (status) => {
    if (!gameId) {
      return;
    }

    await updateGame.mutateAsync({
      status,
      sizzle: undefined,
      bgg: undefined,
      personalGoal: undefined,
      customAuthors: undefined,
      gameType: undefined,
      referenceId: undefined,
      tags: undefined,
      feedbackEnabled: undefined,
    });
  };

  /**
   * @param {string} internalId
   */
  const updateInternalId = async (internalId) => {
    if (isStringEmpty(internalId)) {
      return;
    }

    await updateGame.mutateAsync({
      internalId,
      sizzle: undefined,
      bgg: undefined,
      personalGoal: undefined,
      customAuthors: undefined,
      gameType: undefined,
      referenceId: undefined,
      tags: undefined,
      feedbackEnabled: undefined,
    });
  };

  /**
   * @param {("2025"|"not-set")} personalGoal
   */
  const updatePersonalGoal = async (personalGoal) => {
    await updateGame.mutateAsync({
      personalGoal,
      sizzle: undefined,
      bgg: undefined,
      customAuthors: undefined,
      gameType: undefined,
      referenceId: undefined,
      tags: undefined,
      feedbackEnabled: undefined,
    });
  };

  /**
   * @param {boolean} archived
   */
  const updateArchived = async (archived) => {
    await updateGame.mutateAsync({
      archived,
      sizzle: undefined,
      bgg: undefined,
      personalGoal: undefined,
      customAuthors: undefined,
      gameType: undefined,
      referenceId: undefined,
      tags: undefined,
      feedbackEnabled: undefined,
    });
  };

  /**
   * @param {"game" | "expansion"} gameType
   */
  const updateGameType = async (gameType) => {
    await updateGame.mutateAsync({
      gameType,
      sizzle: undefined,
      bgg: undefined,
      personalGoal: undefined,
      customAuthors: undefined,
      referenceId: undefined,
      tags: undefined,
      feedbackEnabled: undefined,
    });
  };

  useDocumentTitle(game ? game.name : "Pitch2Table");

  if (isLoading) {
    return <CardLoading />;
  }

  if (!game || !gameId) {
    if (error instanceof Error) {
      enqueueSnackbar("Error loading content", { variant: "error" });
    }

    return (
      <Card elevation={1} square={false}>
        <Box p={2} justifyContent="center">
          <ErrorOutline />
        </Box>
      </Card>
    );
  }

  return (
    <>
      <GameStatusCard gameId={gameId} status={game.status} onChange={updateStatus} />
      <Box p={0.5} />
      <Card elevation={1} square={false}>
        <List>
          <Stack direction="row">
            <Stack direction="row" alignItems="center">
              <StarGoalButton personalGoal={game.personalGoal} onToggle={updatePersonalGoal} />
            </Stack>
            <Stack p={1} flex={1} justifyContent="flex-end">
              <EditableInternalId
                title="Project ID"
                text={game.internalId}
                onSave={updateInternalId}
                helperText="The project id is your personal reference for this game. Some designers give their games a special code that never changes which when the game name changes."
              />
            </Stack>
          </Stack>
          <Box p={1}>
            <GameCardCover game={game} editable />
          </Box>
          <Stack direction="row">
            <Box p={2} pt={0}>
              <EditableHeading
                text={game.name}
                onSave={updateName}
                helperText="A good pitch starts with a good name."
                title="Game Name"
              />
            </Box>
          </Stack>
          <Stack direction="row">
            <ListItem>
              <EditPlayersControl game={game} />
            </ListItem>
            <ListItem>
              <EditDurationControl game={game} />
            </ListItem>
            <ListItem>
              <EditAgeControl game={game} />
            </ListItem>
          </Stack>
          <ListItem>
            <EditableListItem
              text={game.tag}
              onSave={updateTag}
              title="Short Pitch"
              helperText="Your game in one sentence."
            />
          </ListItem>
          <ListItem>
            <EditableListItem
              text={game.description}
              onSave={updateDescription}
              helperText="The longer pitch of your game"
              title="Long Pitch"
            />
          </ListItem>
          <ListItem>
            <EditableTagsListItem
              title="Tags"
              text={game.tags}
              onSave={updateTags}
              helperText="Comma-separated. Shown as lowercase chips."
            />
          </ListItem>
        </List>
      </Card>
      <Box p={0.5} />
      <ContractCardForGame game={game} />
      <Box p={0.5} />
      <Card elevation={1} square={false}>
        <List>
          <PublisherInterestForGameMenuItem gameId={gameId} />
          <UploadedSellsheet url={game.sellsheet} gameId={gameId} isPitching={game.status === "PitchingNow"} />
          <UploadedRules url={game.rules} gameId={gameId} />
          <UploadedSizzle url={game.sizzle} gameId={gameId} />
        </List>
      </Card>
      <Box p={0.5} />
      <GameLinksCard gameId={gameId} />
      {Features.OriginalGamePage && <Box p={0.5} />}
      {Features.OriginalGamePage && (
        <Card elevation={1} square={false}>
          <List>
            <MessageBoardMenuItem gameId={game.id} />
          </List>
        </Card>
      )}
      {Features.OriginalGamePage && <Box p={0.5} />}
      {Features.OriginalGamePage && (
        <Card elevation={1} square={false}>
          <List>
            <PlayTestsMenuItem gameId={game.id} />
          </List>
        </Card>
      )}
      {Features.OriginalGamePage && <Box p={0.5} />}
      {Features.OriginalGamePage && (
        <Card elevation={1} square={false}>
          <List>
            <ConventionsMenuItem gameId={game.id} />
          </List>
        </Card>
      )}
      {showContracts(game) && Features.OriginalGamePage && (
        <>
          <Box p={0.5} />
          <RoyaltyCardForGame game={game} />
        </>
      )}
      {["Signed", "Published"].includes(game.status) && (
        <>
          <Box p={0.5} />
          <GameEditionsCard gameId={gameId} />
        </>
      )}
      <Box p={0.5} />
      <LinkedGamesCard gameId={gameId} />
      <Box p={0.5} />
      <GameAwardsCard gameId={gameId} />
      <Box p={0.5} />
      <Card elevation={1} square={false}>
        <List>
          <GameSlugs gameId={gameId} />
          <GameDesignersMenuItem gameId={game.id} designers={game.designers} />
          <GameYearStarted gameId={game.id} />
          <BggRankField gameId={gameId} bgg={game.bgg} />
          <GameTypeControl gameType={game.gameType} onToggle={updateGameType} />
          <ArchiveControl archived={game.archived} onToggle={updateArchived} />
        </List>
      </Card>
      <Box p={0.5} />
      {Features.FinancialModelling && (
        <Card elevation={1} square={false}>
          <List>
            <ArrowListItem primary="Component Configurations" secondary="5 found" to={Path.game(gameId).components()} />
            <ArrowListItem primary="Shipping Lanes" secondary="AU, USA, UK and India" to={Path.game(gameId).lanes()} />
            <ArrowListItem
              primary="Financial Modelling"
              secondary="Combine component configurations, quotes and shipping lanes"
              to={Path.game(gameId).financialModelling()}
            />
          </List>
        </Card>
      )}
      <LastUpdated timestamp={game.updated} />
    </>
  );
};

export const GameDetailFromRoute = () => {
  const gameId = useGameIdFromRoute();
  const { data: games = [], isLoading } = useMyGames();

  if (!gameId) {
    return <EmptyDetailCard text="Select a game from the list on the left." />;
  }

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!isLoading && !games.map((game) => game.id).includes(gameId)) {
    return (
      <Backdrop sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
        <PublicGameDetail gameId={gameId} designerView />
      </Backdrop>
    );
  }

  return <GameDetail gameId={gameId} />;
};
